// SimpleVideoPlayer powers the simple-video demo route, showcasing how the app mixes
// alternate audio tracks with a single video element while exposing a master clock for
// synced UI experiments.
"use strict";

/*
 * SimpleVideoPlayer renders a flexible video experience that can mix the original video audio
 * with any number of alternate audio-only tracks, exposes a master clock for subscribers, and
 * keeps its custom UI (playback controls, timeline, and volume) synchronized across sources.
 *
 * Internally the component maintains refs to a <video> element and an auxiliary <audio> element,
 * tracks shared playback state via React hooks, and drives synchronization through requestAnimationFrame
 * and requestVideoFrameCallback loops. Centralized master clock subscriptions propagate timing
 * updates while effect hooks react to user interactions, ensuring the selected audio source and
 * video element stay frame-accurate during play, pause, seek, and scrubbing operations.
 */

import { useCallback, useEffect, useMemo, useRef, useState, useId } from "react";
import PropTypes from "prop-types";
import { FormControl, Grid, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import VolumeDown from "@mui/icons-material/VolumeDown";
import VolumeUp from "@mui/icons-material/VolumeUp";
import FastRewindIcon from "@mui/icons-material/FastRewind";
import FastForwardIcon from "@mui/icons-material/FastForward";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import { primaryColor } from "../../utils/constants";

const SEEK_INCREMENT_SECONDS = 10;

/**
 * Enumerates the supported master clock sampling rates that subscribers can request.
 * Consumers should reference the exported `SimpleVideoPlayerClockGranularity` helper rather than
 * hard-coding these string literals.
 */
const CLOCK_GRANULARITY_OPTIONS = Object.freeze({
    SECOND: "second",
    HUNDRED_MS: "hundredMs",
    TEN_MS: "tenMs",
    FRAME: "frame",
});

const GRANULARITY_INTERVALS = Object.freeze({
    [CLOCK_GRANULARITY_OPTIONS.SECOND]: 1,
    [CLOCK_GRANULARITY_OPTIONS.HUNDRED_MS]: 0.1,
    [CLOCK_GRANULARITY_OPTIONS.TEN_MS]: 0.01,
});

const DEFAULT_FRAME_DURATION = 1 / 30;
const DEFAULT_AUDIO_OPTION_ID = "video-track";
const DEFAULT_AUDIO_OPTION_LABEL = "Video audio";
const AUDIO_SYNC_THRESHOLD_SECONDS = 0.05;
// HTMLMediaElement.HAVE_CURRENT_DATA indicates playable media is buffered.
const HAVE_CURRENT_DATA_READY_STATE = 2;
const BUFFER_BASE_SECONDS = 2;
const BUFFER_INCREMENT_SECONDS = 1;
const BUFFER_MAX_SECONDS = 10;
const BUFFER_MONITOR_INTERVAL_MS = 250;

/**
 * Format the provided number of seconds into an `hh:mm:ss` or `mm:ss` string.
 *
 * @param {number} seconds
 * @returns {string}
 */
const formatTime = (seconds) => {
    if (!Number.isFinite(seconds)) {
        return "00:00";
    }

    const totalSeconds = Math.max(0, seconds);
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = Math.floor(totalSeconds % 60);

    const hourPrefix = hrs > 0 ? `${String(hrs).padStart(2, "0")}:` : "";
    return `${hourPrefix}${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const PLAYBACK_EVENT_TYPES = Object.freeze({
    STARTED: "playback-started",
    PAUSED: "playback-paused",
    ENDED: "playback-ended",
    SEEKED: "playback-seeked",
    INTERRUPTED_BUFFERING: "playback-interrupted-buffering",
    INTERRUPTED_CONNECTION: "playback-interrupted-connection",
    RESUMED: "playback-resumed",
    BUFFER_SIZE_INCREASED: "buffer-size-increased",
});

const PLAYBACK_EVENTS_WITH_BUFFER_STATE = new Set([
    PLAYBACK_EVENT_TYPES.STARTED,
    PLAYBACK_EVENT_TYPES.PAUSED,
    PLAYBACK_EVENT_TYPES.INTERRUPTED_BUFFERING,
    PLAYBACK_EVENT_TYPES.INTERRUPTED_CONNECTION,
    PLAYBACK_EVENT_TYPES.RESUMED,
]);

/**
 * @typedef {Object} SimpleVideoPlayerClockEvent
 * @property {number} currentTime - The current playback position in seconds.
 * @property {number} delta - The elapsed time since the previous event for the same subscriber.
 * @property {string} granularity - The clock granularity that triggered the event.
 * @property {number} [frame] - The running frame count when using frame-based subscriptions.
 * @property {VideoFrameCallbackMetadata} [metadata] - The metadata provided by `requestVideoFrameCallback`.
 */

/**
 * @typedef {Object} SimpleVideoPlayerClockApi
 * @property {(granularity: string, callback: (event: SimpleVideoPlayerClockEvent) => void, options?: {
 *     fireImmediately?: boolean;
 * }) => () => void} subscribe
 *  Registers a new listener for the requested clock granularity. The returned function unsubscribes.
 * @property {() => number} getCurrentTime - Returns the player's current media time in seconds.
 * @property {() => number} getDuration - Returns the most recent media duration in seconds.
 * @property {() => boolean} isPlaying - Indicates whether the player is actively playing.
 * @property {typeof CLOCK_GRANULARITY_OPTIONS} granularityOptions - Reference to the clock granularity enum.
 */

/**
 * @typedef {Object} SimpleVideoPlayerPlaybackEvent
 * @property {string} type - The emitted playback event type.
 * @property {"user"|"system"} origin - Indicates whether a user action or internal system change triggered the event.
 * @property {number} timestamp - Epoch milliseconds when the event fired.
 * @property {number} currentTime - The current playback position in seconds when the event was emitted.
 * @property {string} mediaElement - Identifies the HTML media element associated with the event ("video" or "audio").
 * @property {string} mediaSourceId - Stable identifier describing which media source triggered the event.
 * @property {string} mediaSourceLabel - Human readable label for the media source.
 * @property {string} activeAudioTrackId - Identifier for the active audio track at the time of emission.
 * @property {string} activeAudioTrackLabel - Readable label for the active audio track at the time of emission.
 * @property {number} [targetTime] - Requested seek target time in seconds when the event represents a seek.
 * @property {number} [requestedTime] - User requested seek time, which may differ from the resolved media time.
 * @property {string} [reason] - Optional explanation for interruption style events.
 * @property {number} [readyState] - The media element ready state when available.
 * @property {number} [errorCode] - Media error code when available for connection failures.
 * @property {string} [errorMessage] - Human readable error description when available.
 * @property {string} [resumedFrom] - Indicates which interruption type resumed when emitting a resume event.
 * @property {number} [bufferSizeSeconds] - The configured client buffer size in seconds when relevant.
 */

/**
 * @typedef {Object} SimpleVideoPlayerPlaybackEventApi
 * @property {(callback: (event: SimpleVideoPlayerPlaybackEvent) => void) => () => void} subscribe
 *  Registers a listener for playback events. Returns an unsubscribe function.
 * @property {typeof PLAYBACK_EVENT_TYPES} eventTypes - Reference to the available playback event types.
 */

/**
 * Render a video element with standard media controls while exposing a master clock API that other
 * components can subscribe to. The clock publishes events at multiple granularities to keep
 * auxiliary UI elements in sync with playback.
 *
 * @param {Object} props
 * @param {string} props.source - Media source URL for the player.
 * @param {string} [props.poster] - Poster image displayed before playback begins.
 * @param {"vertical"|"horizontal"} [props.layout]
 * @param {boolean} [props.enableScroll]
 * @param {Object} [props.customClasses]
 * @param {boolean} [props.autoPlay]
 * @param {boolean} [props.loop]
 * @param {boolean} [props.muted]
 * @param {(clock: SimpleVideoPlayerClockApi|null) => void} [props.onClockReady] - Callback invoked
 * when the master clock becomes available or is torn down.
 * @returns {JSX.Element}
 */
const SimpleVideoPlayer = ({
    source,
    poster,
    layout = "vertical",
    enableScroll = false,
    customClasses = {},
    autoPlay = false,
    loop = false,
    muted = false,
    audioTracks = [],
    videoAudioLabel,
    onClockReady,
    onPlaybackEventsReady,
}) => {
    const videoRef = useRef(null);
    const audioRef = useRef(null);
    const scrubbingRef = useRef(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [selectedAudioSourceId, setSelectedAudioSourceId] = useState(DEFAULT_AUDIO_OPTION_ID);
    const [scrollOffset, setScrollOffset] = useState(enableScroll ? 108 : 0);
    const [bufferedAheadSeconds, setBufferedAheadSeconds] = useState(0);
    const [bufferGoalSeconds, setBufferGoalSeconds] = useState(BUFFER_BASE_SECONDS);
    const subscribersRef = useRef(new Map());
    const animationFrameRef = useRef(null);
    const videoFrameCallbackIdRef = useRef(null);
    const lastFrameMediaTimeRef = useRef(null);
    const estimatedFrameDurationRef = useRef(DEFAULT_FRAME_DURATION);
    const durationRef = useRef(0);
    const isPlayingRef = useRef(false);
    const clockApiRef = useRef(null);
    const playbackSubscribersRef = useRef(new Map());
    const playbackEventApiRef = useRef(null);
    const pendingUserEventRef = useRef(null);
    const lastInterruptionRef = useRef(null);
    const selectedAudioSourceIdRef = useRef(DEFAULT_AUDIO_OPTION_ID);
    const audioSourcesRef = useRef([]);
    const bufferGoalSecondsRef = useRef(BUFFER_BASE_SECONDS);
    const bufferManagementRef = useRef({
        video: {
            autoPaused: false,
            resumeWhenBuffered: false,
            timeoutId: null,
        },
        audio: {
            autoPaused: false,
            resumeWhenBuffered: false,
            timeoutId: null,
        },
    });
    const audioSelectLabelId = useId();

    const resolvedVideoAudioLabel = typeof videoAudioLabel === "string" && videoAudioLabel.trim()
        ? videoAudioLabel.trim()
        : DEFAULT_AUDIO_OPTION_LABEL;

    const normalizedAudioTracks = useMemo(() => {
        if (!Array.isArray(audioTracks)) {
            return [];
        }

        return audioTracks
            .map((track, index) => {
                if (!track || typeof track !== "object") {
                    return null;
                }

                const sourceUrl = typeof track.src === "string" ? track.src.trim() : "";
                if (!sourceUrl) {
                    return null;
                }

                const identifier = typeof track.id === "string" && track.id.trim()
                    ? track.id.trim()
                    : `audio-${index}`;
                const label = typeof track.label === "string" && track.label.trim()
                    ? track.label.trim()
                    : `Audio track ${index + 1}`;

                return {
                    id: identifier,
                    label,
                    src: sourceUrl,
                };
            })
            .filter(Boolean);
    }, [audioTracks]);

    const audioSelectionOptions = useMemo(() => [
        {
            id: DEFAULT_AUDIO_OPTION_ID,
            label: resolvedVideoAudioLabel,
        },
        ...normalizedAudioTracks,
    ], [normalizedAudioTracks, resolvedVideoAudioLabel]);

    /**
     * Helper indicating whether an external audio track is currently active.
     *
     * @returns {boolean}
     */
    const isUsingExternalAudio = useCallback(
        () => selectedAudioSourceIdRef.current !== DEFAULT_AUDIO_OPTION_ID,
        [],
    );

    /**
     * Measure how many seconds of media are buffered ahead of the current playhead for an element.
     *
     * @param {HTMLMediaElement|null} element
     * @returns {number}
     */
    const getBufferedAheadSeconds = useCallback((element) => {
        if (!element || !element.buffered) {
            return 0;
        }

        const currentTime = element.currentTime || 0;
        const { buffered } = element;

        for (let index = 0; index < buffered.length; index += 1) {
            const start = buffered.start(index);
            const end = buffered.end(index);
            if (currentTime >= start && currentTime <= end) {
                return Math.max(0, end - currentTime);
            }
        }

        return 0;
    }, []);

    /**
     * Synchronize the buffered-ahead measurement state for the active media configuration.
     *
     * @returns {number}
     */
    const updateBufferedAhead = useCallback(() => {
        const videoBuffered = getBufferedAheadSeconds(videoRef.current);
        const safeVideoBuffered = Number.isFinite(videoBuffered)
            ? Math.max(0, videoBuffered)
            : 0;

        if (isUsingExternalAudio()) {
            const audioBuffered = getBufferedAheadSeconds(audioRef.current);
            const safeAudioBuffered = Number.isFinite(audioBuffered)
                ? Math.max(0, audioBuffered)
                : 0;
            const effectiveBuffered = Math.min(safeVideoBuffered, safeAudioBuffered);
            setBufferedAheadSeconds(effectiveBuffered);
            return effectiveBuffered;
        }

        setBufferedAheadSeconds(safeVideoBuffered);
        return safeVideoBuffered;
    }, [getBufferedAheadSeconds, isUsingExternalAudio]);

    /**
     * Keep refs synchronized with the latest selection and track metadata so event handlers have
     * access to the most current data without re-registering DOM listeners.
     */
    useEffect(() => {
        selectedAudioSourceIdRef.current = selectedAudioSourceId;
    }, [selectedAudioSourceId]);

    useEffect(() => {
        audioSourcesRef.current = normalizedAudioTracks;
    }, [normalizedAudioTracks]);

    useEffect(() => {
        updateBufferedAhead();
    }, [selectedAudioSourceId, updateBufferedAhead]);

    /**
     * Ensure the selected audio source remains valid if the list of alternate tracks changes.
     */
    useEffect(() => {
        const availableIds = new Set([
            DEFAULT_AUDIO_OPTION_ID,
            ...normalizedAudioTracks.map((track) => track.id),
        ]);

        if (!availableIds.has(selectedAudioSourceIdRef.current)) {
            setSelectedAudioSourceId(DEFAULT_AUDIO_OPTION_ID);
        }
    }, [normalizedAudioTracks]);

    /**
     * When the video source changes, return to the default audio configuration.
     */

    const markPendingUserEvent = (type, metadata = {}) => {
        pendingUserEventRef.current = {
            type,
            metadata,
            expiresAt: Date.now() + 1500,
        };
    };

    const resolvePendingUserEvent = (type) => {
        const pending = pendingUserEventRef.current;
        if (pending && pending.type === type) {
            pendingUserEventRef.current = null;
            return { origin: "user", metadata: pending.metadata };
        }

        if (pending && pending.expiresAt <= Date.now()) {
            pendingUserEventRef.current = null;
        }

        return { origin: "system", metadata: undefined };
    };

    const getActiveAudioTrackDescriptor = useCallback(() => {
        if (selectedAudioSourceIdRef.current === DEFAULT_AUDIO_OPTION_ID) {
            return {
                id: DEFAULT_AUDIO_OPTION_ID,
                label: resolvedVideoAudioLabel,
            };
        }

        const track = audioSourcesRef.current.find(
            (entry) => entry.id === selectedAudioSourceIdRef.current,
        );

        if (track) {
            return {
                id: track.id,
                label: track.label,
            };
        }

        return {
            id: selectedAudioSourceIdRef.current,
            label: "Alternate audio",
        };
    }, [resolvedVideoAudioLabel]);

    const describeMediaSource = useCallback((element) => {
        if (element && element === audioRef.current && isUsingExternalAudio()) {
            const activeTrack = getActiveAudioTrackDescriptor();
            return {
                mediaElement: "audio",
                mediaSourceId: activeTrack.id,
                mediaSourceLabel: activeTrack.label,
            };
        }

        return {
            mediaElement: "video",
            mediaSourceId: DEFAULT_AUDIO_OPTION_ID,
            mediaSourceLabel: resolvedVideoAudioLabel,
        };
    }, [getActiveAudioTrackDescriptor, isUsingExternalAudio, resolvedVideoAudioLabel]);

    const emitPlaybackEvent = useCallback((type, overrides = {}, element = videoRef.current) => {
        if (!Object.values(PLAYBACK_EVENT_TYPES).includes(type)) {
            return;
        }

        if (!playbackSubscribersRef.current.size) {
            return;
        }

        const descriptor = describeMediaSource(element);
        const activeAudio = getActiveAudioTrackDescriptor();
        const video = videoRef.current;
        const payload = {
            type,
            origin: overrides.origin || "system",
            timestamp: Date.now(),
            currentTime: video ? video.currentTime || 0 : 0,
            mediaElement: descriptor.mediaElement,
            mediaSourceId: descriptor.mediaSourceId,
            mediaSourceLabel: descriptor.mediaSourceLabel,
            activeAudioTrackId: activeAudio.id,
            activeAudioTrackLabel: activeAudio.label,
            ...overrides,
        };

        if (PLAYBACK_EVENTS_WITH_BUFFER_STATE.has(type)) {
            const bufferedAhead = updateBufferedAhead();
            payload.bufferedAheadSeconds = Number.isFinite(bufferedAhead)
                ? Math.max(0, bufferedAhead)
                : 0;
        }

        playbackSubscribersRef.current.forEach((callback) => {
            try {
                callback(payload);
            } catch (error) {
                // Intentionally swallow listener errors to prevent cascading failures.
            }
        });
    }, [describeMediaSource, getActiveAudioTrackDescriptor, updateBufferedAhead]);

    const resolveBufferManagerEntry = useCallback((element) => {
        if (!element) {
            return null;
        }

        if (element === audioRef.current) {
            return bufferManagementRef.current.audio;
        }

        return bufferManagementRef.current.video;
    }, []);

    const cancelBufferMonitor = useCallback((element) => {
        const entry = resolveBufferManagerEntry(element);
        if (!entry || entry.timeoutId === null) {
            return;
        }

        clearTimeout(entry.timeoutId);
        entry.timeoutId = null;
    }, [resolveBufferManagerEntry]);

    const clearAutoBufferingForMedia = useCallback((element) => {
        const entry = resolveBufferManagerEntry(element);
        if (!entry) {
            return;
        }

        cancelBufferMonitor(element);
        entry.autoPaused = false;
        entry.resumeWhenBuffered = false;
    }, [cancelBufferMonitor, resolveBufferManagerEntry]);

    const enforceBufferGoalForMedia = useCallback((element) => {
        if (!element) {
            return;
        }

        const entry = resolveBufferManagerEntry(element);
        if (!entry) {
            return;
        }

        const bufferGoalSeconds = bufferGoalSecondsRef.current;
        if (!Number.isFinite(bufferGoalSeconds) || bufferGoalSeconds <= 0) {
            clearAutoBufferingForMedia(element);
            return;
        }

        const bufferedAhead = getBufferedAheadSeconds(element);
        if (bufferedAhead >= bufferGoalSeconds || element.ended) {
            const shouldResume = entry.autoPaused && entry.resumeWhenBuffered;
            entry.autoPaused = false;
            entry.resumeWhenBuffered = false;
            cancelBufferMonitor(element);

            if (shouldResume && element.paused) {
                const playPromise = element.play();
                if (playPromise && typeof playPromise.catch === "function") {
                    playPromise.catch(() => {});
                }
            }
            return;
        }

        if (!entry.autoPaused) {
            const wasPlaying = !element.paused && !element.ended;
            entry.autoPaused = true;
            entry.resumeWhenBuffered = wasPlaying;

            if (wasPlaying) {
                try {
                    element.pause();
                } catch (error) {
                    // Ignore pause errors that can occur on restricted platforms.
                }
            }
        }

        cancelBufferMonitor(element);
        entry.timeoutId = setTimeout(() => {
            entry.timeoutId = null;
            enforceBufferGoalForMedia(element);
        }, BUFFER_MONITOR_INTERVAL_MS);
    }, [cancelBufferMonitor, clearAutoBufferingForMedia, getBufferedAheadSeconds, resolveBufferManagerEntry]);

    const applyBufferGoalToMediaElements = useCallback((bufferSeconds) => {
        const safeBufferSeconds = clamp(bufferSeconds, BUFFER_BASE_SECONDS, BUFFER_MAX_SECONDS);
        const mediaElements = [videoRef.current, audioRef.current];

        mediaElements.forEach((media) => {
            if (!media) {
                return;
            }

            try {
                if (media.preload !== "auto") {
                    media.preload = "auto";
                }
            } catch (error) {
                // Ignore preload assignment failures on unsupported browsers.
            }

            if (media.dataset) {
                media.dataset.bufferGoalSeconds = String(safeBufferSeconds);
                media.dataset.appBufferGoalSeconds = String(safeBufferSeconds);
            }

            if (typeof media.setAttribute === "function") {
                media.setAttribute("data-buffer-goal-seconds", String(safeBufferSeconds));
                media.setAttribute("data-app-buffer-goal-seconds", String(safeBufferSeconds));
            }

            if (typeof media.readyState === "number" && media.readyState === 0 && typeof media.load === "function") {
                try {
                    media.load();
                } catch (error) {
                    // Ignore load errors that arise from unsupported states.
                }
            }
            if (media.readyState > 0) {
                enforceBufferGoalForMedia(media);
            }
        });
    }, [enforceBufferGoalForMedia]);

    // Keep buffer configuration synchronized across the managed media elements.
    useEffect(() => {
        applyBufferGoalToMediaElements(bufferGoalSecondsRef.current);
        setBufferGoalSeconds(bufferGoalSecondsRef.current);
    }, [applyBufferGoalToMediaElements]);

    const handleSevereBuffering = useCallback((element) => {
        const currentBufferGoal = bufferGoalSecondsRef.current;
        if (currentBufferGoal >= BUFFER_MAX_SECONDS) {
            return;
        }

        const nextBufferGoal = clamp(
            currentBufferGoal + BUFFER_INCREMENT_SECONDS,
            BUFFER_BASE_SECONDS,
            BUFFER_MAX_SECONDS,
        );

        if (nextBufferGoal === currentBufferGoal) {
            return;
        }

        bufferGoalSecondsRef.current = nextBufferGoal;
        setBufferGoalSeconds(nextBufferGoal);
        applyBufferGoalToMediaElements(nextBufferGoal);
        enforceBufferGoalForMedia(element);
        emitPlaybackEvent(
            PLAYBACK_EVENT_TYPES.BUFFER_SIZE_INCREASED,
            {
                origin: "system",
                bufferSizeSeconds: nextBufferGoal,
            },
            element,
        );
    }, [applyBufferGoalToMediaElements, emitPlaybackEvent, enforceBufferGoalForMedia]);

    // Reset to the default audio selection when the primary media source changes.
    useEffect(() => {
        setSelectedAudioSourceId(DEFAULT_AUDIO_OPTION_ID);
    }, [source, clearAutoBufferingForMedia, enforceBufferGoalForMedia, handleSevereBuffering]);

    /**
     * Attempt to align the external audio track with the provided timestamp.
     *
     * @param {number} targetTime
     * @param {{ force?: boolean }} [options]
     */
    const syncExternalAudioTime = useCallback((targetTime, options = {}) => {
        const audio = audioRef.current;
        if (!audio || !isUsingExternalAudio() || !Number.isFinite(targetTime)) {
            return;
        }

        if (!options.force && typeof audio.readyState === "number" && audio.readyState < HAVE_CURRENT_DATA_READY_STATE) {
            return;
        }

        const difference = Math.abs((audio.currentTime || 0) - targetTime);
        if (!options.force && difference <= AUDIO_SYNC_THRESHOLD_SECONDS) {
            return;
        }

        try {
            audio.currentTime = targetTime;
        } catch (error) {
            // Some browsers disallow seeking before metadata is available. Ignore these attempts.
        }
    }, [isUsingExternalAudio]);

    /**
     * Mirror the video's playback state on the external audio element when required.
     */
    const startExternalAudioPlayback = useCallback(() => {
        if (!isUsingExternalAudio()) {
            return;
        }

        const video = videoRef.current;
        const audio = audioRef.current;
        if (!video || !audio) {
            return;
        }

        syncExternalAudioTime(video.currentTime || 0, { force: true });
        audio.volume = volume;
        audio.playbackRate = video.playbackRate || 1;
        audio.play().catch(() => {
            // Suppress autoplay errors; the UI playback state will remain accurate.
        });
    }, [isUsingExternalAudio, syncExternalAudioTime, volume]);

    const pauseExternalAudioPlayback = useCallback(() => {
        if (!isUsingExternalAudio()) {
            return;
        }

        const audio = audioRef.current;
        if (audio) {
            audio.pause();
        }
    }, [isUsingExternalAudio]);

    /**
     * Handle selection changes from the audio source dropdown.
     *
     * @param {{ target: { value: unknown } }} event
     */
    const handleAudioSourceChange = (event) => {
        const { value } = event.target;
        const nextId = typeof value === "string" ? value : String(value);
        setSelectedAudioSourceId(nextId);
    };

    /**
     * Keep the rendered volume slider synchronized with whichever media element is active.
     */
    useEffect(() => {
        const video = videoRef.current;
        const audio = audioRef.current;

        if (selectedAudioSourceId !== DEFAULT_AUDIO_OPTION_ID) {
            setVolume(audio ? audio.volume : 1);
            return;
        }

        setVolume(video ? video.volume : 1);
    }, [selectedAudioSourceId]);

    /**
     * Keep the hidden audio element configured with the selected alternate track.
     */
    useEffect(() => {
        const video = videoRef.current;
        const audio = audioRef.current;
        if (!video || !audio) {
            return undefined;
        }

        const usingExternal = selectedAudioSourceId !== DEFAULT_AUDIO_OPTION_ID;

        if (!usingExternal) {
            pauseExternalAudioPlayback();
            audio.removeAttribute("src");
            audio.load();
            video.muted = muted;
            applyBufferGoalToMediaElements(bufferGoalSecondsRef.current);
            return undefined;
        }

        const selectedTrack = audioSourcesRef.current.find(
            (track) => track.id === selectedAudioSourceId,
        );

        if (!selectedTrack) {
            setSelectedAudioSourceId(DEFAULT_AUDIO_OPTION_ID);
            return undefined;
        }

        video.muted = true;
        audio.loop = loop;

        if (audio.src !== selectedTrack.src) {
            audio.src = selectedTrack.src;
            audio.load();
            applyBufferGoalToMediaElements(bufferGoalSecondsRef.current);
        }

        const handleReady = () => {
            syncExternalAudioTime(video.currentTime || 0, { force: true });
            setVolume(audio.volume);
            if (!video.paused && !video.ended) {
                startExternalAudioPlayback();
            }
            updateBufferedAhead();
        };

        audio.addEventListener("loadedmetadata", handleReady);
        audio.addEventListener("canplay", handleReady);

        const handleAudioWaiting = () => {
            lastInterruptionRef.current = {
                element: "audio",
                type: PLAYBACK_EVENT_TYPES.INTERRUPTED_BUFFERING,
            };
            emitPlaybackEvent(
                PLAYBACK_EVENT_TYPES.INTERRUPTED_BUFFERING,
                {
                    origin: "system",
                    reason: "buffering",
                    readyState: audio.readyState,
                },
                audio,
            );
            handleSevereBuffering(audio);
            enforceBufferGoalForMedia(video);
            updateBufferedAhead();
        };

        const handleAudioError = () => {
            const mediaError = audio.error;
            lastInterruptionRef.current = {
                element: "audio",
                type: PLAYBACK_EVENT_TYPES.INTERRUPTED_CONNECTION,
            };
            emitPlaybackEvent(
                PLAYBACK_EVENT_TYPES.INTERRUPTED_CONNECTION,
                {
                    origin: "system",
                    reason: "connection-failure",
                    errorCode: mediaError ? mediaError.code : undefined,
                    errorMessage: mediaError && mediaError.message ? mediaError.message : undefined,
                },
                audio,
            );
            updateBufferedAhead();
        };

        const handleAudioPlaying = () => {
            if (lastInterruptionRef.current && lastInterruptionRef.current.element === "audio") {
                emitPlaybackEvent(
                    PLAYBACK_EVENT_TYPES.RESUMED,
                    {
                        origin: "system",
                        resumedFrom: lastInterruptionRef.current.type,
                    },
                    audio,
                );
                lastInterruptionRef.current = null;
            }
            clearAutoBufferingForMedia(audio);
            updateBufferedAhead();
        };

        const enforceAudioBufferGoal = () => {
            enforceBufferGoalForMedia(audio);
            updateBufferedAhead();
        };

        audio.addEventListener("waiting", handleAudioWaiting);
        audio.addEventListener("error", handleAudioError);
        audio.addEventListener("playing", handleAudioPlaying);
        audio.addEventListener("progress", enforceAudioBufferGoal);

        if (audio.readyState >= 1) {
            handleReady();
        }

        return () => {
            audio.removeEventListener("loadedmetadata", handleReady);
            audio.removeEventListener("canplay", handleReady);
            audio.removeEventListener("waiting", handleAudioWaiting);
            audio.removeEventListener("error", handleAudioError);
            audio.removeEventListener("playing", handleAudioPlaying);
            audio.removeEventListener("progress", enforceAudioBufferGoal);
            clearAutoBufferingForMedia(audio);
        };
    }, [
        selectedAudioSourceId,
        loop,
        muted,
        applyBufferGoalToMediaElements,
        clearAutoBufferingForMedia,
        emitPlaybackEvent,
        enforceBufferGoalForMedia,
        handleSevereBuffering,
        pauseExternalAudioPlayback,
        startExternalAudioPlayback,
        syncExternalAudioTime,
        updateBufferedAhead,
    ]);

    /**
     * Mirror manual volume adjustments performed on the external audio element.
     */
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) {
            return undefined;
        }

        const handleAudioVolumeChange = () => {
            if (isUsingExternalAudio()) {
                setVolume(audio.volume);
            }
        };

        audio.addEventListener("volumechange", handleAudioVolumeChange);

        return () => {
            audio.removeEventListener("volumechange", handleAudioVolumeChange);
        };
    }, [isUsingExternalAudio]);

    useEffect(() => {
        const audio = audioRef.current;
        if (audio) {
            audio.loop = loop;
        }
    }, [loop]);

    useEffect(() => () => {
        const audio = audioRef.current;
        if (audio) {
            audio.pause();
        }
    }, []);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) {
            return;
        }

        video.muted = muted || selectedAudioSourceId !== DEFAULT_AUDIO_OPTION_ID;
    }, [muted, selectedAudioSourceId]);

    /**
     * Coerce incoming granularity values into a known option, defaulting to second-level updates.
     *
     * @param {string} granularity
     * @returns {string}
     */
    const normalizeGranularity = (granularity) => {
        if (typeof granularity !== "string") {
            return CLOCK_GRANULARITY_OPTIONS.SECOND;
        }

        const trimmed = granularity.trim();
        if (!trimmed) {
            return CLOCK_GRANULARITY_OPTIONS.SECOND;
        }

        if (Object.values(CLOCK_GRANULARITY_OPTIONS).includes(trimmed)) {
            return trimmed;
        }

        const lowerCased = trimmed.toLowerCase();
        const caseInsensitiveMatch = Object.values(CLOCK_GRANULARITY_OPTIONS).find(
            (option) => option.toLowerCase() === lowerCased,
        );

        return caseInsensitiveMatch || CLOCK_GRANULARITY_OPTIONS.SECOND;
    };

    /**
     * Reset the tracking state for every clock subscriber so the next tick is treated as fresh.
     */
    const resetClockSubscribers = useCallback(() => {
        subscribersRef.current.forEach((subscriber) => {
            subscriber.lastTime = undefined;
            subscriber.lastFrame = undefined;
            subscriber.lastFrameCount = 0;
        });
    }, []);

    /**
     * Optionally emit the current playback position to a new subscriber so it has immediate context.
     *
     * @param {Object} subscriber
     * @param {boolean} fireImmediately
     */
    const notifyInitialClockState = (subscriber, fireImmediately) => {
        const video = videoRef.current;
        const currentTimeValue = video ? video.currentTime || 0 : 0;

        // Record the most recent timestamp (and frame metadata when available) even when
        // we're deferring the first callback. This prevents interval-based subscribers
        // from immediately firing on the next animation frame with a zero delta.
        subscriber.lastTime = currentTimeValue;

        if (subscriber.granularity === CLOCK_GRANULARITY_OPTIONS.FRAME) {
            const playbackQuality = video && typeof video.getVideoPlaybackQuality === "function"
                ? video.getVideoPlaybackQuality()
                : undefined;
            const initialFrameCount = playbackQuality && typeof playbackQuality.totalVideoFrames === "number"
                ? playbackQuality.totalVideoFrames
                : 0;
            subscriber.lastFrame = initialFrameCount;
            subscriber.lastFrameCount = initialFrameCount;

            if (!fireImmediately) {
                return;
            }

            subscriber.callback({
                currentTime: currentTimeValue,
                frame: initialFrameCount,
                delta: 0,
                granularity: subscriber.granularity,
            });
            return;
        }

        if (!fireImmediately) {
            return;
        }

        subscriber.callback({
            currentTime: currentTimeValue,
            delta: 0,
            granularity: subscriber.granularity,
        });
    };

    if (!clockApiRef.current) {
        clockApiRef.current = {
            /**
             * Subscribe to the master clock.
             *
             * @param {string} granularity
             * @param {(event: SimpleVideoPlayerClockEvent) => void} callback
             * @param {{ fireImmediately?: boolean }} [options]
             * @returns {() => void}
             */
            subscribe: (granularity, callback, options = {}) => {
                if (typeof callback !== "function") {
                    return () => {};
                }

                const normalizedGranularity = normalizeGranularity(granularity);
                const fireImmediately = options.fireImmediately !== false;
                const subscriberId = Symbol("clock-subscriber");
                const subscriberRecord = {
                    granularity: normalizedGranularity,
                    callback,
                    lastTime: undefined,
                    lastFrame: undefined,
                    lastFrameCount: 0,
                };

                subscribersRef.current.set(subscriberId, subscriberRecord);
                notifyInitialClockState(subscriberRecord, fireImmediately);

                return () => {
                    subscribersRef.current.delete(subscriberId);
                };
            },
            /** @returns {number} */
            getCurrentTime: () => {
                const video = videoRef.current;
                return video ? video.currentTime || 0 : 0;
            },
            /** @returns {number} */
            getDuration: () => durationRef.current,
            /** @returns {boolean} */
            isPlaying: () => isPlayingRef.current,
            granularityOptions: CLOCK_GRANULARITY_OPTIONS,
        };
    }

    if (!playbackEventApiRef.current) {
        playbackEventApiRef.current = {
            subscribe: (callback) => {
                if (typeof callback !== "function") {
                    return () => {};
                }

                const subscriberId = Symbol("playback-subscriber");
                playbackSubscribersRef.current.set(subscriberId, callback);

                return () => {
                    playbackSubscribersRef.current.delete(subscriberId);
                };
            },
            eventTypes: PLAYBACK_EVENT_TYPES,
        };
    }

    useEffect(() => {
        durationRef.current = duration || 0;
    }, [duration]);

    useEffect(() => {
        isPlayingRef.current = isPlaying;
    }, [isPlaying]);

    useEffect(() => {
        if (typeof onClockReady !== "function") {
            return undefined;
        }

        const clockApi = clockApiRef.current;
        onClockReady(clockApi);

        return () => {
            onClockReady(null);
        };
    }, [onClockReady]);

    useEffect(() => {
        if (typeof onPlaybackEventsReady !== "function") {
            return undefined;
        }

        const playbackApi = playbackEventApiRef.current;
        onPlaybackEventsReady(playbackApi);

        return () => {
            onPlaybackEventsReady(null);
        };
    }, [onPlaybackEventsReady]);

    useEffect(() => {
        let cancelled = false;

        const runAnimationLoop = () => {
            // Read the most up-to-date media time so interval-based subscribers stay in sync.
            const video = videoRef.current;
            const currentTimeValue = video ? video.currentTime || 0 : 0;

            // Dispatch updates for second / millisecond-based subscribers.
            subscribersRef.current.forEach((subscriber) => {
                if (subscriber.granularity === CLOCK_GRANULARITY_OPTIONS.FRAME) {
                    if (video && typeof video.requestVideoFrameCallback === "function") {
                        return;
                    }

                    // Fallback frame tracking by elapsed time when frame callbacks are unavailable.
                    const lastTime = subscriber.lastTime ?? -Infinity;
                    if (currentTimeValue < lastTime) {
                        subscriber.lastTime = currentTimeValue;
                        subscriber.lastFrameCount = 0;
                        return;
                    }

                    const frameDuration = estimatedFrameDurationRef.current || DEFAULT_FRAME_DURATION;
                    if (lastTime === -Infinity || currentTimeValue - lastTime >= frameDuration) {
                        const delta = lastTime === -Infinity ? 0 : currentTimeValue - lastTime;
                        subscriber.lastTime = currentTimeValue;
                        subscriber.lastFrameCount = (subscriber.lastFrameCount || 0) + 1;
                        subscriber.callback({
                            currentTime: currentTimeValue,
                            frame: subscriber.lastFrameCount,
                            delta,
                            granularity: subscriber.granularity,
                        });
                    }
                    return;
                }

                const intervalSeconds = GRANULARITY_INTERVALS[subscriber.granularity];
                if (!intervalSeconds) {
                    return;
                }

                // Publish the event once the interval threshold has been exceeded.
                const lastTime = subscriber.lastTime ?? -Infinity;
                if (currentTimeValue < lastTime) {
                    subscriber.lastTime = currentTimeValue;
                    return;
                }

                if (lastTime === -Infinity || currentTimeValue - lastTime >= intervalSeconds) {
                    const delta = lastTime === -Infinity ? 0 : currentTimeValue - lastTime;
                    subscriber.lastTime = currentTimeValue;
                    subscriber.callback({
                        currentTime: currentTimeValue,
                        delta,
                        granularity: subscriber.granularity,
                    });
                }
            });

            // Keep the animation loop alive while the component is mounted.
            if (!cancelled) {
                animationFrameRef.current = window.requestAnimationFrame(runAnimationLoop);
            }
        };

        animationFrameRef.current = window.requestAnimationFrame(runAnimationLoop);

        const scheduleFrameCallback = () => {
            const video = videoRef.current;
            if (!video || typeof video.requestVideoFrameCallback !== "function") {
                return;
            }

            const frameCallback = (now, metadata) => {
                if (cancelled) {
                    return;
                }

                // Derive the most accurate media time we can for frame granularity subscribers.
                const activeVideo = videoRef.current;
                const currentTimeValue = metadata && typeof metadata.mediaTime === "number"
                    ? metadata.mediaTime
                    : activeVideo
                        ? activeVideo.currentTime || 0
                        : 0;

                if (metadata && typeof metadata.mediaTime === "number") {
                    // Track the observed frame duration for browsers lacking `presentedFrames`.
                    if (lastFrameMediaTimeRef.current !== null) {
                        const delta = metadata.mediaTime - lastFrameMediaTimeRef.current;
                        if (delta > 0) {
                            estimatedFrameDurationRef.current = delta;
                        }
                    }
                    lastFrameMediaTimeRef.current = metadata.mediaTime;
                }

                subscribersRef.current.forEach((subscriber) => {
                    if (subscriber.granularity !== CLOCK_GRANULARITY_OPTIONS.FRAME) {
                        return;
                    }

                    const presentedFrames = metadata && typeof metadata.presentedFrames === "number"
                        ? metadata.presentedFrames
                        : undefined;

                    if (presentedFrames !== undefined) {
                        // Use the browser-provided frame counter for precise frame events.
                        if (subscriber.lastFrame === undefined || presentedFrames > subscriber.lastFrame) {
                            const delta = subscriber.lastTime !== undefined ? currentTimeValue - subscriber.lastTime : 0;
                            subscriber.lastFrame = presentedFrames;
                            subscriber.lastTime = currentTimeValue;
                            subscriber.lastFrameCount = presentedFrames;
                            subscriber.callback({
                                currentTime: currentTimeValue,
                                frame: presentedFrames,
                                delta,
                                granularity: subscriber.granularity,
                                metadata,
                            });
                        }
                        return;
                    }

                    // Approximate frame transitions by elapsed time when explicit counts are unavailable.
                    const lastTime = subscriber.lastTime ?? -Infinity;
                    if (currentTimeValue < lastTime) {
                        subscriber.lastTime = currentTimeValue;
                        subscriber.lastFrameCount = 0;
                        return;
                    }

                    const frameDuration = estimatedFrameDurationRef.current || DEFAULT_FRAME_DURATION;
                    if (lastTime === -Infinity || currentTimeValue - lastTime >= frameDuration) {
                        const delta = lastTime === -Infinity ? 0 : currentTimeValue - lastTime;
                        subscriber.lastTime = currentTimeValue;
                        subscriber.lastFrameCount = (subscriber.lastFrameCount || 0) + 1;
                        subscriber.callback({
                            currentTime: currentTimeValue,
                            frame: subscriber.lastFrameCount,
                            delta,
                            granularity: subscriber.granularity,
                            metadata,
                        });
                    }
                });

                // Continuously subscribe to the next frame while mounted.
                scheduleFrameCallback();
            };

            videoFrameCallbackIdRef.current = video.requestVideoFrameCallback(frameCallback);
        };

        scheduleFrameCallback();

        const cleanupVideo = videoRef.current;

        return () => {
            cancelled = true;
            if (animationFrameRef.current) {
                window.cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }

            if (cleanupVideo && typeof cleanupVideo.cancelVideoFrameCallback === "function" && videoFrameCallbackIdRef.current !== null) {
                cleanupVideo.cancelVideoFrameCallback(videoFrameCallbackIdRef.current);
            }
            videoFrameCallbackIdRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (layout === "horizontal" && enableScroll) {
            const handleScroll = () => {
                if (window.scrollY < 95) {
                    setScrollOffset(108 - window.scrollY);
                } else {
                    setScrollOffset(10);
                }
            };

            handleScroll();
            window.addEventListener("scroll", handleScroll);
            return () => window.removeEventListener("scroll", handleScroll);
        }

        setScrollOffset(0);
        return undefined;
    }, [layout, enableScroll]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) {
            return undefined;
        }

        // Reset tracking state whenever the media source changes.
        resetClockSubscribers();
        estimatedFrameDurationRef.current = DEFAULT_FRAME_DURATION;
        lastFrameMediaTimeRef.current = null;
        updateBufferedAhead();

        // Sync duration, current time, and estimated frame data whenever metadata loads.
        const handleLoadedMetadata = () => {
            const safeDuration = video.duration || 0;
            const startingTime = video.currentTime || 0;
            setDuration(safeDuration);
            durationRef.current = safeDuration;
            setCurrentTime(startingTime);
            estimatedFrameDurationRef.current = DEFAULT_FRAME_DURATION;
            lastFrameMediaTimeRef.current = null;
            resetClockSubscribers();
            syncExternalAudioTime(startingTime, { force: true });
            updateBufferedAhead();
        };

        // Mirror the `<video>` playback position while the user is not scrubbing.
        const handleTimeUpdate = () => {
            if (!scrubbingRef.current) {
                const mediaTime = video.currentTime || 0;
                setCurrentTime(mediaTime);
                syncExternalAudioTime(mediaTime);
                updateBufferedAhead();
            }
        };

        // Toggle UI state as playback changes.
        const handlePlay = () => {
            setIsPlaying(true);
            startExternalAudioPlayback();
            const { origin } = resolvePendingUserEvent("play");
            emitPlaybackEvent(PLAYBACK_EVENT_TYPES.STARTED, { origin }, video);
            lastInterruptionRef.current = null;
        };
        const handlePause = () => {
            setIsPlaying(false);
            pauseExternalAudioPlayback();
            const { origin } = resolvePendingUserEvent("pause");
            emitPlaybackEvent(PLAYBACK_EVENT_TYPES.PAUSED, { origin }, video);
            if (origin === "user") {
                clearAutoBufferingForMedia(video);
            }
        };
        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(video.duration || 0);
            resetClockSubscribers();
            syncExternalAudioTime(video.duration || 0, { force: true });
            pauseExternalAudioPlayback();
            emitPlaybackEvent(PLAYBACK_EVENT_TYPES.ENDED, { origin: "system" }, video);
            lastInterruptionRef.current = null;
        };

        const handleSeeked = () => {
            const resolution = resolvePendingUserEvent("seek");
            const targetTime = video.currentTime || 0;
            emitPlaybackEvent(
                PLAYBACK_EVENT_TYPES.SEEKED,
                {
                    origin: resolution.origin,
                    targetTime,
                    requestedTime: resolution.metadata && Number.isFinite(resolution.metadata.requestedTime)
                        ? resolution.metadata.requestedTime
                        : undefined,
                },
                video,
            );
        };

        const handleWaiting = () => {
            lastInterruptionRef.current = {
                element: "video",
                type: PLAYBACK_EVENT_TYPES.INTERRUPTED_BUFFERING,
            };
            emitPlaybackEvent(
                PLAYBACK_EVENT_TYPES.INTERRUPTED_BUFFERING,
                {
                    origin: "system",
                    reason: "buffering",
                    readyState: video.readyState,
                },
                video,
            );
            handleSevereBuffering(video);
            updateBufferedAhead();
        };

        const handleVideoError = () => {
            const mediaError = video.error;
            lastInterruptionRef.current = {
                element: "video",
                type: PLAYBACK_EVENT_TYPES.INTERRUPTED_CONNECTION,
            };
            emitPlaybackEvent(
                PLAYBACK_EVENT_TYPES.INTERRUPTED_CONNECTION,
                {
                    origin: "system",
                    reason: "connection-failure",
                    errorCode: mediaError ? mediaError.code : undefined,
                    errorMessage: mediaError && mediaError.message ? mediaError.message : undefined,
                },
                video,
            );
        };

        const handlePlaying = () => {
            if (lastInterruptionRef.current && lastInterruptionRef.current.element === "video") {
                emitPlaybackEvent(
                    PLAYBACK_EVENT_TYPES.RESUMED,
                    {
                        origin: "system",
                        resumedFrom: lastInterruptionRef.current.type,
                    },
                    video,
                );
                lastInterruptionRef.current = null;
            }
            clearAutoBufferingForMedia(video);
            updateBufferedAhead();
        };

        // Surface volume adjustments performed outside the React controls.
        const handleVolumeChange = () => {
            if (!isUsingExternalAudio()) {
                setVolume(video.volume);
            }
        };

        const handleVideoProgress = () => {
            enforceBufferGoalForMedia(video);
            updateBufferedAhead();
        };

        // Attach DOM event listeners for core media events.
        video.addEventListener("loadedmetadata", handleLoadedMetadata);
        video.addEventListener("timeupdate", handleTimeUpdate);
        video.addEventListener("play", handlePlay);
        video.addEventListener("pause", handlePause);
        video.addEventListener("ended", handleEnded);
        video.addEventListener("volumechange", handleVolumeChange);
        video.addEventListener("seeked", handleSeeked);
        video.addEventListener("waiting", handleWaiting);
        video.addEventListener("error", handleVideoError);
        video.addEventListener("playing", handlePlaying);
        video.addEventListener("progress", handleVideoProgress);
        applyBufferGoalToMediaElements(bufferGoalSecondsRef.current);

        // Prime state when the media is already loaded (e.g., cached sources).
        if (video.readyState >= 1) {
            handleLoadedMetadata();
        }

        setIsPlaying(!video.paused && !video.ended);
        setVolume(video.volume);

        // Remove listeners when the component unmounts or the source changes.
        return () => {
            video.removeEventListener("loadedmetadata", handleLoadedMetadata);
            video.removeEventListener("timeupdate", handleTimeUpdate);
            video.removeEventListener("play", handlePlay);
            video.removeEventListener("pause", handlePause);
            video.removeEventListener("ended", handleEnded);
            video.removeEventListener("volumechange", handleVolumeChange);
            video.removeEventListener("seeked", handleSeeked);
            video.removeEventListener("waiting", handleWaiting);
            video.removeEventListener("error", handleVideoError);
            video.removeEventListener("playing", handlePlaying);
            video.removeEventListener("progress", handleVideoProgress);
            clearAutoBufferingForMedia(video);
        };
    }, [
        source,
        applyBufferGoalToMediaElements,
        clearAutoBufferingForMedia,
        emitPlaybackEvent,
        enforceBufferGoalForMedia,
        handleSevereBuffering,
        isUsingExternalAudio,
        pauseExternalAudioPlayback,
        resetClockSubscribers,
        startExternalAudioPlayback,
        syncExternalAudioTime,
        updateBufferedAhead,
    ]);

    const containerStyles = layout === "horizontal"
        ? {
            width: "100%",
            top: `${scrollOffset}px`,
            position: "sticky",
            zIndex: 1000,
            ...(customClasses.containerStyles || {}),
        }
        : customClasses.containerStyles || {};

    const batteryFillRatio = useMemo(() => {
        if (!Number.isFinite(bufferGoalSeconds) || bufferGoalSeconds <= 0) {
            return 0;
        }

        const ratio = bufferedAheadSeconds / bufferGoalSeconds;
        if (ratio >= 0.95) {
            return 1;
        }

        return clamp(ratio, 0, 1);
    }, [bufferGoalSeconds, bufferedAheadSeconds]);

    const bufferSummaryLabel = useMemo(() => {
        const safeBuffered = Number.isFinite(bufferedAheadSeconds)
            ? Math.max(0, bufferedAheadSeconds)
            : 0;
        const safeGoal = Number.isFinite(bufferGoalSeconds)
            ? Math.max(0, bufferGoalSeconds)
            : 0;

        return `${Math.round(safeBuffered)}s / ${Math.round(safeGoal)}s`;
    }, [bufferGoalSeconds, bufferedAheadSeconds]);

    const attemptPlay = () => {
        const video = videoRef.current;
        if (!video) {
            return;
        }

        const handleSuccess = () => {
            startExternalAudioPlayback();
            setIsPlaying(true);
        };

        const playPromise = video.play();
        if (playPromise && typeof playPromise.then === "function") {
            playPromise
                .then(() => {
                    handleSuccess();
                })
                .catch(() => {
                    setIsPlaying(false);
                    pauseExternalAudioPlayback();
                });
            return;
        }

        handleSuccess();
    };

    const handlePlayPause = () => {
        const video = videoRef.current;
        if (!video) {
            return;
        }

        if (video.paused || video.ended) {
            markPendingUserEvent("play");
            attemptPlay();
        } else {
            markPendingUserEvent("pause");
            video.pause();
            setIsPlaying(false);
            pauseExternalAudioPlayback();
        }
    };

    const handleSeek = (event, value) => {
        const nextTime = Array.isArray(value) ? value[0] : value;
        scrubbingRef.current = true;
        setCurrentTime(nextTime);
    };

    const handleSeekCommit = (event, value) => {
        const nextTime = Array.isArray(value) ? value[0] : value;
        const video = videoRef.current;
        scrubbingRef.current = false;
        if (!video) {
            setCurrentTime(nextTime);
            return;
        }

        const safeTime = clamp(nextTime, 0, duration || 0);
        markPendingUserEvent("seek", { requestedTime: safeTime });
        video.currentTime = safeTime;
        setCurrentTime(safeTime);
        syncExternalAudioTime(safeTime, { force: true });
        resetClockSubscribers();
    };

    const handleVolumeSliderChange = (event, value) => {
        const newVolume = Array.isArray(value) ? value[0] : value;
        const video = videoRef.current;
        const audio = audioRef.current;
        const safeVolume = clamp(Number(newVolume) || 0, 0, 1);
        const target = selectedAudioSourceId === DEFAULT_AUDIO_OPTION_ID ? video : audio;
        if (video) {
            video.volume = safeVolume;
        }
        if (target && target !== video) {
            target.volume = safeVolume;
        }
        setVolume(safeVolume);
    };

    const handleSkip = (direction) => {
        const video = videoRef.current;
        if (!video) {
            return;
        }

        const next = clamp(
            video.currentTime + direction * SEEK_INCREMENT_SECONDS,
            0,
            duration || video.duration || 0,
        );
        markPendingUserEvent("seek", { requestedTime: next });
        video.currentTime = next;
        setCurrentTime(next);
        syncExternalAudioTime(next, { force: true });
        resetClockSubscribers();
    };

    return (
        <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={layout === "horizontal" ? 12 : 5.85}
            xl={layout === "horizontal" ? 12 : 5.85}
            container
            direction="row"
            height={layout === "horizontal" ? "auto" : "715px"}
            style={containerStyles}
            className={customClasses.container}
        >
            <Grid
                item
                container
                className={customClasses.videoContainer}
                sx={{ position: "relative" }}
            >
                <video
                    ref={videoRef}
                    width="100%"
                    height="100%"
                    src={source}
                    poster={poster}
                    controls={false}
                    autoPlay={autoPlay}
                    loop={loop}
                    muted={muted}
                    style={{ backgroundColor: "#000" }}
                />
                <Box
                    role="img"
                    aria-label={`Buffered ${bufferSummaryLabel}`}
                    sx={{
                        position: "absolute",
                        bottom: 16,
                        right: 16,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        backgroundColor: "#000",
                        borderRadius: "999px",
                        padding: "6px 10px",
                        opacity: 0.7,
                        color: "#fff",
                        pointerEvents: "none",
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                width: 32,
                                height: 14,
                                border: "2px solid currentColor",
                                borderRadius: "3px",
                                padding: "2px",
                                boxSizing: "border-box",
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    width: `${batteryFillRatio * 100}%`,
                                    height: "100%",
                                    backgroundColor: "currentColor",
                                    borderRadius: "1px",
                                    transition: "width 120ms ease-out",
                                }}
                            />
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                width: 4,
                                height: 10,
                                border: "2px solid currentColor",
                                borderLeft: 0,
                                borderRadius: "0 2px 2px 0",
                                marginLeft: "2px",
                                backgroundColor: batteryFillRatio > 0 ? "currentColor" : "transparent",
                            }}
                        />
                    </Box>
                    <Typography
                        variant="caption"
                        component="span"
                        sx={{ fontWeight: 500, color: "inherit" }}
                    >
                        {bufferSummaryLabel}
                    </Typography>
                </Box>
                <audio
                    ref={audioRef}
                    preload="auto"
                    style={{ display: "none" }}
                    aria-hidden="true"
                />
            </Grid>

            <Grid
                item
                container
                className={customClasses.controlsContainer}
                sx={{ padding: "16px", minHeight: "120px" }}
            >
                <Grid item xs={12} sx={{ paddingX: 1 }}>
                    <Slider
                        value={duration ? currentTime : 0}
                        min={0}
                        max={duration || 0}
                        step={0.01}
                        onChange={handleSeek}
                        onChangeCommitted={handleSeekCommit}
                        aria-label="Progress"
                        sx={{ color: `${primaryColor}` }}
                        disabled={!duration}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={2}
                        flexWrap="wrap"
                        sx={{ paddingX: 1 }}
                    >
                        <IconButton
                            aria-label="Rewind"
                            onClick={() => handleSkip(-1)}
                            sx={{ color: `${primaryColor}` }}
                        >
                            <FastRewindIcon fontSize="large" />
                        </IconButton>

                        <IconButton
                            aria-label={isPlaying ? "Pause" : "Play"}
                            onClick={handlePlayPause}
                            sx={{ color: `${primaryColor}` }}
                        >
                            {isPlaying ? (
                                <PauseCircleIcon fontSize="large" />
                            ) : (
                                <PlayCircleIcon fontSize="large" />
                            )}
                        </IconButton>

                        <IconButton
                            aria-label="Fast forward"
                            onClick={() => handleSkip(1)}
                            sx={{ color: `${primaryColor}` }}
                        >
                            <FastForwardIcon fontSize="large" />
                        </IconButton>

                        <Typography variant="body2" sx={{ minWidth: "120px" }}>
                            {`${formatTime(currentTime)} / ${formatTime(duration)}`}
                        </Typography>

                        <FormControl
                            size="small"
                            sx={{ minWidth: "140px", maxWidth: "200px", flex: "0 1 200px" }}
                        >
                            <InputLabel id={`${audioSelectLabelId}-label`}>Audio source</InputLabel>
                            <Select
                                labelId={`${audioSelectLabelId}-label`}
                                id={`${audioSelectLabelId}-select`}
                                value={selectedAudioSourceId}
                                label="Audio source"
                                onChange={handleAudioSourceChange}
                            >
                                {audioSelectionOptions.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ width: 200 }}
                        >
                            <VolumeDown />
                            <Slider
                                value={volume}
                                min={0}
                                max={1}
                                step={0.05}
                                onChange={handleVolumeSliderChange}
                                aria-label="Volume"
                                sx={{ color: `${primaryColor}` }}
                            />
                            <VolumeUp />
                        </Stack>
                    </Stack>
                </Grid>
            </Grid>
        </Grid>
    );
};

SimpleVideoPlayer.propTypes = {
    source: PropTypes.string.isRequired,
    poster: PropTypes.string,
    layout: PropTypes.oneOf(["vertical", "horizontal"]),
    enableScroll: PropTypes.bool,
    customClasses: PropTypes.shape({
        containerStyles: PropTypes.object,
        container: PropTypes.string,
        videoContainer: PropTypes.string,
        controlsContainer: PropTypes.string,
    }),
    autoPlay: PropTypes.bool,
    loop: PropTypes.bool,
    muted: PropTypes.bool,
    audioTracks: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        src: PropTypes.string.isRequired,
    })),
    videoAudioLabel: PropTypes.string,
    onClockReady: PropTypes.func,
    onPlaybackEventsReady: PropTypes.func,
};

/**
 * Static convenience reference so class-style access mirrors the named export.
 *
 * @type {typeof CLOCK_GRANULARITY_OPTIONS}
 */
SimpleVideoPlayer.ClockGranularity = CLOCK_GRANULARITY_OPTIONS;

/**
 * Static enumeration of playback event types for external consumers.
 *
 * @type {typeof PLAYBACK_EVENT_TYPES}
 */
SimpleVideoPlayer.PlaybackEventType = PLAYBACK_EVENT_TYPES;

/**
 * Exported alias for the available master clock granularities.
 */
export const SimpleVideoPlayerClockGranularity = CLOCK_GRANULARITY_OPTIONS;

/**
 * Exported alias for the general playback event types.
 */
export const SimpleVideoPlayerPlaybackEventType = PLAYBACK_EVENT_TYPES;

export default SimpleVideoPlayer;
